/**
 *
 * This is the entry-way into the routing logic. This is the first component that's called when the app
 * loads.
 *
 */
import { Component, ViewEncapsulation, OnInit, Inject, OnDestroy } from "@angular/core";
import { Router, ActivatedRoute, NavigationStart } from "@angular/router";
import { environment } from "../environments/environment"
import { GatewayService } from "./service/gateway.service";
import { WebstorageService } from "./service/webstorage.service";
import { DomSanitizer } from "@angular/platform-browser";

import * as OktaSignIn from '@okta/okta-signin-widget';
import { OktaAuth, SigninWithRedirectOptions } from '@okta/okta-auth-js';
import { OktaAuthStateService, OKTA_AUTH } from '@okta/okta-angular';

import { Config, Tokens, TokenStorage, UserManager, TokenManager, FRUser } from '@forgerock/javascript-sdk';

import { MsalService } from "@azure/msal-angular";
import '@aquera/nile-elements';
import * as _ from 'lodash';
import { EventType } from "@azure/msal-browser";
import { has, isEmpty, isObject } from "lodash";
import { ToasterService } from "./service/toaster.service";
import { TranslateService } from "@ngx-translate/core";
import { AuthSessionService } from "./service/auth-session.service";
import { TAB_KEYS, getTabLanguage, getTabValue, cleanupLegacySharedStorage, legacyMsalCacheKeys, jwtSubject } from "./service/tab-session";
export let browserRefresh = false;

const DEFAULT_ORIGINAL_URI = window.location.origin;
@Component({
    selector: 'app-root',
    styleUrls: ['./app.component.scss'],
    templateUrl: 'template/app.html',
    encapsulation: ViewEncapsulation.None
})

export class AppComponent implements OnInit, OnDestroy {
    public loading: boolean = true;
    signIn: any;
    isAuthenticated: any;
    windowIsError = false;

    subscription: any;
    _ForgeRockCode = "";
    _ForgeRockState = "";

    public currentLang: string = 'jp';
    selectedOrganizationId: string = '';

    constructor(private translate: TranslateService , public router: Router, public gateway: GatewayService, private route: ActivatedRoute, public storage: WebstorageService, private domSanitizer: DomSanitizer, private azureAuthService: MsalService, @Inject(OKTA_AUTH) public oktaAuth: OktaAuth, public authService: OktaAuthStateService, public toast: ToasterService, private authSession: AuthSessionService) {
        // Organization, tenant and SDK tokens used to be shared by every tab in localStorage; they are dropped, never adopted.
        cleanupLegacySharedStorage([
            ...legacyMsalCacheKeys(environment.AD_CONFIG['azure']['clientId']),
            'okta-token-storage',
            'forgerock-sdk-' + environment.AD_CONFIG.forgerock["FORGEROCK_WEB_OAUTH_CLIENT"],
        ]);
        this.currentLang = getTabLanguage();
        this.translate.setDefaultLang(this.currentLang);
        this.translate.use(this.currentLang);
    }

    async ngOnInit() {

        // Before anything reads ForgeRock tokens: they are kept per tab, like the MSAL and Okta ones.
        this.configureForgeRock();

        this.route.queryParams.subscribe(async params => {
            if(params?.selectedOrganizationId) {
                // An explicit link to an organization; the API still checks the user may access it.
                this.selectedOrganizationId = params?.selectedOrganizationId;
                this.storage.setPartner(this.selectedOrganizationId);
            }

            if (params?.code != undefined && params?.state != undefined && (environment.DIRECTORY === 'ForgeRock DS' || environment.DIRECTORY === 'ForgeRock_IM' || environment.DIRECTORY === 'ForgeRock_IC')) {
                this._ForgeRockState = params.state;
                this._ForgeRockCode = params.code;
                this.handleForgeRockAuthentication(true);
            } else {
                this.handleForgeRockAuthentication(false);
            }
        });

        if (environment._ISERROR) {
            this.windowIsError = true;
            this.router.navigate(['home/noclient']);
        }

        if (this.signIn) {
            this.signIn.remove();
        }

        if (environment.DIRECTORY === 'Azure') {
            // Set active acccount on page load
            // Only this tab's accounts are cached (sessionStorage); prefer the one this tab was using.
            const accounts = this.azureAuthService.instance.getAllAccounts();
            if (accounts.length > 0) {
                const tabAccountId = getTabValue(TAB_KEYS.accountId);
                this.azureAuthService.instance.setActiveAccount(accounts.find(a => a.homeAccountId === tabAccountId) || accounts[0]);
            }

            this.azureAuthService.instance.addEventCallback((event) => {
                if (has(event, 'error') && !isEmpty(event.error) && isObject(event.error) && has(event.error, 'errorMessage')) {
                    this.loading = false;
                    if (event.error.errorCode === 'interaction_required' || event.error.name === 'InteractionRequiredAuthError') {
                        this.storage.removeAllData();
                        this.azureLogin();
                    } else {
                        this.router.navigate(["home/error"], { state: { statusCode: event.error.name, body: event.error.errorCode, errorMessage: event.error.errorMessage } });
                    }
                }
                // set active account after redirect
                if (event.eventType === EventType.LOGIN_SUCCESS && event.payload.account) {
                    const account = event.payload.account;
                    this.azureAuthService.instance.setActiveAccount(account);
                    this.authSession.bindAccount(account.homeAccountId);
                    //this.router.navigate(['/securehome/partner-users']);
                    this.router.navigate(["home/select-partner"]);
                }
            })

            // handle auth redired/do all initial setup for msal
            this.azureAuthService.instance.handleRedirectPromise().then(authResult => {
                // Check if user signed in 
                const account = this.azureAuthService.instance.getActiveAccount();
                if (!account) {
                    // redirect anonymous user to login page 
                    this.azureLogin();
                } else {
                    this.authSession.bindAccount(account.homeAccountId);
                    this.restorePartnerOrSelect();
                }
            }).catch(error => {
                if (error && (error.errorCode === 'interaction_required' || error.name === 'InteractionRequiredAuthError')) {
                    this.storage.removeAllData();
                    this.azureLogin();
                } else {
                    console.error('Auth error:', error);
                    this.azureLogin();
                }
            })
        } else if (environment.DIRECTORY === 'Okta') {
            this.oktaAuth.isAuthenticated().then((isAuthenticated) => {
                if (isAuthenticated) {
                    this.isAuthenticated = isAuthenticated;
                    this.authSession.bindAccount(jwtSubject(this.oktaAuth.getIdToken()));
                    this.watchOktaRenewals();
                    this.restorePartnerOrSelect();
                } else {
                    this.oktaAuth.signInWithRedirect(this.authSession.takeAccountPrompt() ? { prompt: 'login' } : undefined);
                }
            });
        }
    }

    async handleForgeRockAuthentication(callback) {
        /*
        * If CallBack Then Try To Get Final Token
        */
        if (environment.DIRECTORY === 'ForgeRock DS' ||  environment.DIRECTORY === 'ForgeRock_IM' || environment.DIRECTORY === 'ForgeRock_IC') {
            if (callback) {
                let tokens = await TokenManager.getTokens({
                    query: {
                        code: this._ForgeRockCode, // Authorization code from redirect URL
                        state: this._ForgeRockState, // State from redirect URL
                    },
                });
                this.authSession.bindAccount(tokens ? jwtSubject(tokens.idToken) : null);
                setTimeout(() => {
                    // this.router.navigate(["home/select-partner"]);
                    this.forgeRockSuccessNavigation();
                }, 1000);

            } else {
                const tokens: Tokens = await TokenStorage.get();
                if (tokens !== undefined) {
                    /*
                    * Check Local For Token If Available Redirect To Next Page
                    */
                    const currentTime = new Date().valueOf();
                    const tokenExpiryTime = tokens.tokenExpiry;
                    if(currentTime > tokenExpiryTime) {
                        this.logout();
                    } else {
                        this.authSession.bindAccount(jwtSubject(tokens.idToken));
                        this.forgeRockSuccessNavigation();
                    }
                } else {
                    /*
                    * For New User Send User To New Login Flow
                    */
                    TokenManager.getTokens({ forceRenew: true, login: 'redirect', support: 'modern' });
                }
            }
        }
    }

    forgeRockSuccessNavigation() {
        this.router.navigate(["home/select-partner"]);
    }

    /** The tab's ForgeRock tokens expired: drop them and sign in again. The AM session other tabs use is left alone. */
    async logout() {
        await TokenStorage.remove();
        this.resetPartnerAndTenantId();
        this.router.navigateByUrl('/');
    }

    resetPartnerAndTenantId() {
        // An organization given in the link is kept; anything else is chosen again on the select page.
        if (this.selectedOrganizationId) {
            this.storage.removeData('_TenantID');
            environment._TenantID = null;
            return;
        }
        this.storage.clearPartner();
    }

    /** After a refresh or redirect, continue with this tab's organization; with none (or on the root page), choose one. */
    restorePartnerOrSelect() {
        const partnerId = this.storage.getData('_PartnerID');
        if ((window.location.pathname !== '/') && partnerId) {
            this.storage.setPartner(partnerId);
        } else {
            this.resetPartnerAndTenantId();
            this.router.navigate(["home/select-partner"]);
        }
    }

    azureLogin() {
        this.azureAuthService.instance.loginRedirect(this.authSession.takeAccountPrompt() ? { scopes: [], prompt: 'select_account' } : undefined);
    }

    /**
     * Okta keeps one browser session for all tabs. If a different user signs in elsewhere, renewing this tab's tokens
     * would quietly switch it to that user; sign this tab out instead.
     */
    watchOktaRenewals() {
        this.oktaAuth.tokenManager.on('renewed', (key, token: any) => {
            if (key !== 'idToken') return;
            const tabAccountId = getTabValue(TAB_KEYS.accountId);
            const renewedAccountId = jwtSubject(token?.idToken);
            if (tabAccountId && renewedAccountId && renewedAccountId !== tabAccountId) {
                this.authSession.signOutThisTab();
            }
        });
    }

    configureForgeRock() {
        Config.set({
            clientId: environment.AD_CONFIG.forgerock["FORGEROCK_WEB_OAUTH_CLIENT"],
            redirectUri: window.location.origin + "/",
            scope: 'openid profile email',
            serverConfig: {
                baseUrl: environment.AD_CONFIG.forgerock["AM_URL"],
                timeout: 60000, // 90000 or less
            },
            realmPath: environment.AD_CONFIG.forgerock["FORGEROCK_REALM_PATH"],
            tree: environment.AD_CONFIG.forgerock["FORGEROCK_JOURNEY_LOGIN"],
            tokenStore: 'sessionStorage',
        });
    }


    ngOnDestroy() {
        if (this.signIn) {
            this.signIn.remove();
        }

        if (this.subscription) {
            this.subscription.unsubscribe();
        }
    }

}